import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import ConfirmTierModal from 'src/components/LeadAgentAcceptanceBanner/modals/ConfirmTierModal/ConfirmTierModal.component';
import UnlockTrialCapModal from 'src/components/LeadAgentAcceptanceBanner/modals/UnlockTrialCapModal/UnlockTrialCapModal.component';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import type {
  tLeadAgentUnlockOffer,
  tLeadAgentUnlockOption,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';

// RET-1633 (U9/U10): 'unlock' opens the U10 UnlockTrialCapModal (option 1 —
// immediate charge that extends/unlocks). 'acceptDeferred' reuses the existing
// ConfirmTierModal (deferred pre-accept = "pay at trial end"). Decline is NOT a
// view here: unlock-decline is NOT the add-on decline (R12), so it must never
// open the add-on DeclineModal (which PATCHes state=PAUSED) — it just closes,
// the offer stays firm. U10 owns any dedicated decline confirmation.
type tModalView = 'closed' | 'unlock' | 'acceptDeferred';

export type tLeadAgentTrialCapActions = {
  onOptionClick: (option: tLeadAgentUnlockOption) => void;
  onContactSupport: () => void;
  isNonMonthly: boolean;
  /**
   * True while an unlock/accept modal is open. A HOST that can dismiss
   * itself (the alert detail modal) must not close while this is true, or
   * its unmount takes the nested modal down with it — mid-charge, that
   * bypasses the REL-2 guard and strands the Owner without a payment
   * confirmation.
   */
  isNestedModalOpen: boolean;
  /** The unlock/accept modals — render wherever the options render. */
  actionModals: ReactNode;
};

/**
 * The CTA view machine for the Lead Agent trial-cap options (RET-1633 U9+U10),
 * extracted from `LeadAgentTrialCapBannerContainer` in RET-1878 so the alert
 * detail modal can offer the SAME unlock/accept flows the banner offers —
 * one copy of the wiring, two consumers. Every guard below shipped with the
 * container and moves verbatim:
 *
 * - `unlock_now`      → opens the U10 UnlockTrialCapModal (immediate charge,
 *                       3DS, no-grace failure UX), rendering from an offer
 *                       SNAPSHOT (REL-2). RET-2329: a background
 *                       `refreshAcceptance` no longer blanks the offer to
 *                       null while this modal is open (KTD1 — a refresh keeps
 *                       the previous value on error and only replaces it once
 *                       the new one lands), and even a legitimate offer
 *                       change no longer unmounts the host detail, which
 *                       always paints `{actionModals}` now regardless of its
 *                       view flags (KTD4). The snapshot stays the belt here;
 *                       the suspenders moved one layer up.
 * - `accept_deferred` → the existing C2 ConfirmTierModal deferred-accept flow.
 * - `decline`         → no state write (R12: unlock-decline ≠ add-on decline);
 *                       the offer stays firm, so the click simply dismisses.
 * - contact support   → opens the (already app-booted) Intercom messenger for
 *                       the extended_capped CS referral (no self-serve unlock).
 *
 * A workspace switch closes the unlock modal so it never retargets the new
 * workspace's offer (adv-front-4); if a charge is mid-flight the close is
 * deferred until the modal's own guard resets it (REL-2).
 */
export function useLeadAgentTrialCapActions(): tLeadAgentTrialCapActions {
  const { currentWorkspace } = useWorkspaceContext();
  const { subscription } = useSubscriptionContext();
  const { trialCapOffer, settleTrialCapUnlock } = useLeadAgentAcceptanceContext();
  const [view, setView] = useState<tModalView>('closed');
  const [offerSnapshot, setOfferSnapshot] =
    useState<tLeadAgentUnlockOffer | null>(null);

  const workspaceId = currentWorkspace?.id ?? '';
  // Fail-safe to monthly while the subscription is still loading: a bare
  // `!== MONTHLY` treats `undefined` as non-monthly and would flash the wrong
  // renewal note during load (adv-front-3).
  const isNonMonthly =
    subscription?.frequency != null &&
    subscription.frequency !== ePlanFrequency.MONTHLY;

  const isModalProcessingRef = useRef(false);
  const pendingCloseOnWsChange = useRef(false);

  const closeUnlockModal = useCallback(() => {
    pendingCloseOnWsChange.current = false;
    setOfferSnapshot(null);
    setView('closed');
  }, []);

  const handleProcessingChange = useCallback(
    (isProcessing: boolean) => {
      isModalProcessingRef.current = isProcessing;
      // A workspace switch deferred the close while a charge was mid-flight;
      // once the modal's mid-await guard resets out of 'processing', honor it.
      if (!isProcessing && pendingCloseOnWsChange.current) {
        closeUnlockModal();
      }
    },
    [closeUnlockModal],
  );

  // Keyed ONLY on workspaceId — adding `view` would fire the instant the
  // modal opens and close it immediately.
  useEffect(() => {
    if (view !== 'unlock') return;
    if (isModalProcessingRef.current) {
      pendingCloseOnWsChange.current = true;
      return;
    }
    closeUnlockModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const onOptionClick = useCallback(
    (option: tLeadAgentUnlockOption) => {
      if (option === 'unlock_now') {
        setOfferSnapshot(trialCapOffer);
        setView('unlock');
        return;
      }
      if (option === 'accept_deferred') {
        setView('acceptDeferred');
        return;
      }
      // 'decline' — R12: dismiss only, no state write, the offer stays firm.
      setView('closed');
    },
    [trialCapOffer],
  );

  const onContactSupport = useCallback(() => {
    // The messenger is booted app-wide; open a fresh conversation if present.
    if (typeof window.Intercom === 'function') {
      window.Intercom('showNewMessage', '');
    }
  }, []);

  const handleUnlocked = useCallback(() => {
    // Optimistically settle so the just-paid Owner stops seeing the sighting/
    // capped trial-cap surfaces before the provider's offer refetch catches up
    // with the webhook (learnings-1 / adv-front-1). Settle first, then close.
    settleTrialCapUnlock();
    closeUnlockModal();
  }, [settleTrialCapUnlock, closeUnlockModal]);

  const actionModals = (
    <>
      {view === 'unlock' && offerSnapshot ? (
        <UnlockTrialCapModal
          workspaceId={workspaceId}
          offer={offerSnapshot}
          isNonMonthly={isNonMonthly}
          onClose={closeUnlockModal}
          onUnlocked={handleUnlocked}
          onProcessingChange={handleProcessingChange}
        />
      ) : null}

      {view === 'acceptDeferred' ? (
        <ConfirmTierModal
          onClose={() => setView('closed')}
          onConfirmed={() => setView('closed')}
        />
      ) : null}
    </>
  );

  return {
    onOptionClick,
    onContactSupport,
    isNonMonthly,
    isNestedModalOpen: view !== 'closed',
    actionModals,
  };
}
